import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getMyMentions, markMentionsSeen, type MentionNotification } from '@/api/users';

/**
 * @mentions for the suite shell, kept apart from the general notifications
 * feed: their own list (not limited to the feed's 7-day window) and their own
 * read state, so looking at one never clears the other's unread markers.
 */
export function useSuiteMentions({ enabled = true }: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['my-mentions'],
    // The API's max. The Mentions tab lists every unread mention before marking
    // them seen, so this caps how many can be shown rather than silently cleared.
    queryFn: () => getMyMentions(50),
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const markSeen = useCallback(() => {
    if (!data?.unread_count) return;
    markMentionsSeen()
      .then(() => queryClient.invalidateQueries({ queryKey: ['my-mentions'] }))
      .catch(() => {
        // Best-effort, like notifications: worst case they stay marked as new.
      });
  }, [data?.unread_count, queryClient]);

  const clickMention = useCallback((mention: MentionNotification) => {
    // Comments live in the campaign status modal, same as comment notifications.
    window.location.href = `/?campaign=${encodeURIComponent(mention.campaign_id)}&action=status`;
  }, []);

  return {
    mentions: data?.mentions ?? [],
    unreadCount: data?.unread_count ?? 0,
    lastSeenAt: data?.last_seen_at ?? null,
    markSeen,
    clickMention,
  };
}
